import { sm2, sm4 } from 'sm-crypto';

export const sm2PublicKey =
  '04c6ac37ebd9c4dede032e8cf3cdaf7c96f53c6ec1eceb86a1fd97226c8955d728ce2536528511bdaebc4b4671744f586dc278ada52b884b9af2e1c49bd0d4fa60';

let cachedSM4Key = null;

/**
 * 生成随机 SM4 密钥 (16字节，返回32位十六进制字符串)
 */
export function generateSM4Key() {
  if (cachedSM4Key) {
    return cachedSM4Key;
  }

  const key = [];
  for (let i = 0; i < 16; i++) {
    key.push(Math.floor(Math.random() * 256));
  }
  cachedSM4Key = Array.from(key)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
  return cachedSM4Key;
}

/**
 * 生成随机 SM4 IV (用于 CBC 模式，16字节)
 */
export function generateSM4IV() {
  return generateSM4Key(); // 同样长度
}

/**
 * SM2 加密
 * @param {string} plainText 明文
 * @param {string} publicKey SM2 公钥 (十六进制)
 * @returns {string} 加密后的密文 (十六进制)
 */
export function sm2Encrypt(plainText, publicKey) {
  // 确保公钥以 04 开头 (非压缩格式)
  const formattedKey = publicKey.startsWith('04') ? publicKey : '04' + publicKey;
  // cipherMode = 1 表示使用 C1C3C2 模式
  let result = sm2.doEncrypt(plainText, formattedKey, 1);
  return '04' + result;
}

/**
 * SM4 加密
 * @param {string} plainText 明文
 * @param {string} key 32位十六进制密钥
 * @param {string} iv 32位十六进制 IV (可选)
 * @returns {string} Base64 格式密文
 */
export function sm4Encrypt(plainText, key, iv = null) {
  const options = { mode: 'ecb' };
  if (iv) {
    options.iv = iv;
  }
  // sm-crypto 的 sm4.encrypt 返回十六进制字符串，需要转换为 Base64
  const hexResult = sm4.encrypt(plainText, key, options);
  return hexToBase64(hexResult);
}

/**
 * SM4 解密
 * @param {string} cipherText Base64 格式密文
 * @param {string} key 32位十六进制密钥
 * @param {string} iv 32位十六进制 IV (可选)
 * @returns {string} 明文
 */
export function sm4Decrypt(cipherText, key, iv = null) {
  const options = { mode: 'ecb' };
  if (iv) {
    options.iv = iv;
  }
  // 先将 Base64 转换为十六进制
  const hexCipher = base64ToHex(cipherText);
  return sm4.decrypt(hexCipher, key, options);
}

// 工具函数：十六进制转 Base64
function hexToBase64(hexString) {
  const bytes = new Uint8Array(hexString.match(/.{1,2}/g).map((byte) => parseInt(byte, 16)));
  let binary = '';
  bytes.forEach((byte) => (binary += String.fromCharCode(byte)));
  return btoa(binary);
}

// 工具函数：Base64 转十六进制
function base64ToHex(base64) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

// 导出 sm-crypto 原生方法供需要时使用
export { sm2, sm4 };
